import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Label } from "../ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Coins, Loader2, Users, Trash2, Plus, ArrowRight, X, Clock, MapPin, Crosshair, Send, Check, AlertCircle, DollarSign, UserPlus } from "lucide-react";

export default function ApplyExpeditionModal({
  showApplyExpedition, setShowApplyExpedition, applyForm, setApplyForm,
  handleApplyExpedition, actionLoading, userCharacters, availableJobs, toggleCharacterSelection
}) {
  return (
      <Dialog open={!!showApplyExpedition} onOpenChange={() => { setShowApplyExpedition(null); setApplyForm({ selected_characters: [], message: "" }); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" style={{ background: "linear-gradient(180deg, #0d1b4a 0%, #0a0f2c 100%)", border: "2px solid #3949ab" }}>
          <DialogHeader>
            <DialogTitle className="text-blue-300 flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Apply to Expedition
            </DialogTitle>
            <DialogDescription className="text-blue-200/60">
              {showApplyExpedition?.boss_name} - Select character(s) to bring
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Required Jobs Display */}
            {showApplyExpedition && !showApplyExpedition.free_for_all && showApplyExpedition.job_requirements?.length > 0 && (
              <div className="p-3 rounded-lg" style={{ background: "rgba(124,77,255,0.1)", border: "1px solid rgba(124,77,255,0.3)" }}>
                <p className="text-xs text-purple-300 uppercase tracking-wider mb-2">Looking For</p>
                <div className="flex flex-wrap gap-2">
                  {showApplyExpedition.job_requirements.map((req, i) => {
                    const jobData = availableJobs[req.job] || {};
                    const skills = jobData.skills || [];
                    return (
                      <div key={i} className="flex items-center gap-1 px-2 py-1 rounded bg-indigo-500/20 border border-indigo-500/40">
                        {skills.map((skill, si) => (
                          <img key={si} src={skill.icon} alt={skill.name} className="w-5 h-5" title={`${req.job} - ${skill.name}`} />
                        ))}
                        <span className="text-xs text-indigo-200">{req.job} {req.count > 1 ? `x${req.count}` : ""}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Character Selection */}
            <div>
              <Label className="text-blue-200 font-semibold mb-2 block">Select Your Character(s)</Label>
              {userCharacters.length > 0 ? (
                <div className="space-y-2">
                  {userCharacters.map((char) => {
                    const isSelected = applyForm.selected_characters.some(c => c.character_id === char.id);
                    const charSkills = char.skills || availableJobs[char.job]?.skills || [];
                    return (
                      <div
                        key={char.id}
                        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                          isSelected
                            ? "ring-2 ring-blue-400"
                            : "hover:bg-blue-500/10"
                        }`}
                        style={{
                          background: isSelected ? "rgba(59,130,246,0.2)" : "rgba(0,0,0,0.3)",
                          border: `1px solid ${isSelected ? "rgba(59,130,246,0.6)" : "rgba(59,130,246,0.2)"}`
                        }}
                        onClick={() => toggleCharacterSelection(char)}
                        data-testid={`apply-char-${char.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex gap-1">
                            {charSkills.map((skill, si) => (
                              <img key={si} src={skill.icon} alt={skill.name} className="w-6 h-6" title={skill.name} />
                            ))}
                          </div>
                          <div>
                            <p className="text-blue-100 font-medium">
                              {char.name || char.job}
                            </p>
                            <p className="text-xs text-blue-300/70">
                              {char.job} - Lv.{char.level}
                            </p>
                          </div>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          isSelected ? "bg-blue-500 border-blue-400" : "border-blue-500/40"
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-6 text-blue-300/60">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">No characters registered yet.</p>
                  <p className="text-xs mt-1">Go to your Profile &gt; Characters tab to add characters first.</p>
                </div>
              )}
            </div>

            {/* Message */}
            <div>
              <Label className="text-blue-200">Message to Leader (Optional)</Label>
              <Textarea
                placeholder="Introduce yourself or add notes..."
                value={applyForm.message}
                onChange={(e) => setApplyForm({ ...applyForm, message: e.target.value })}
                className="bg-black/40 border-blue-500/40 text-white"
                rows={2}
                data-testid="apply-message-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowApplyExpedition(null); setApplyForm({ selected_characters: [], message: "" }); }} className="text-gray-300">
              Cancel
            </Button>
            <Button
              style={{ background: "linear-gradient(180deg, #7c4dff 0%, #5c35cc 100%)", border: "2px solid #9c7cff" }}
              onClick={() => handleApplyExpedition(showApplyExpedition.id)}
              disabled={applyForm.selected_characters.length === 0 && userCharacters.length > 0}
              data-testid="apply-expedition-submit-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Apply ({applyForm.selected_characters.length} char{applyForm.selected_characters.length !== 1 ? "s" : ""})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
}
